import KeyboardArrowLeftOutlined from '@mui/icons-material/KeyboardArrowLeftOutlined';
import KeyboardArrowRightOutlined from '@mui/icons-material/KeyboardArrowRightOutlined';
import styles from './Pagination.module.css';

export interface PaginationProps {
	page: number;
	totalPages: number;
	onPageChange: (page: number) => void;
}

const MAX_VISIBLE = 5;

function buildPageRange(page: number, totalPages: number): (number | '...')[] {
	if (totalPages <= MAX_VISIBLE) {
		return Array.from({ length: totalPages }, (_, i) => i + 1);
	}

	const half = Math.floor(MAX_VISIBLE / 2);
	let start = Math.max(1, page - half);
	const end = Math.min(totalPages, start + MAX_VISIBLE - 1);

	if (end - start < MAX_VISIBLE - 1) {
		start = Math.max(1, end - MAX_VISIBLE + 1);
	}

	const range: (number | '...')[] = [];
	if (start > 1) {
		range.push(1);
		if (start > 2) range.push('...');
	}
	for (let i = start; i <= end; i++) range.push(i);
	if (end < totalPages) {
		if (end < totalPages - 1) range.push('...');
		range.push(totalPages);
	}
	return range;
}

export const Pagination = ({ page, totalPages, onPageChange }: PaginationProps) => {
	if (totalPages <= 1) return null;

	const range = buildPageRange(page, totalPages);

	return (
		<nav className={styles.pagination} aria-label="Paginação">
			<button
				type="button"
				className={styles.navBtn}
				disabled={page <= 1}
				onClick={() => onPageChange(page - 1)}
				aria-label="Página anterior"
			>
				<KeyboardArrowLeftOutlined className={styles.navIcon} />
			</button>

			<div className={styles.pages}>
				{range.map((item, idx) =>
					item === '...' ? (
						<span key={`ellipsis-${idx}`} className={styles.ellipsis}>
							…
						</span>
					) : (
						<button
							key={item}
							type="button"
							className={`${styles.pageBtn} ${item === page ? styles.pageBtnActive : ''}`}
							onClick={() => onPageChange(item)}
							aria-label={`Página ${item}`}
							aria-current={item === page ? 'page' : undefined}
						>
							{item}
						</button>
					)
				)}
			</div>

			<button
				type="button"
				className={styles.navBtn}
				disabled={page >= totalPages}
				onClick={() => onPageChange(page + 1)}
				aria-label="Próxima página"
			>
				<KeyboardArrowRightOutlined className={styles.navIcon} />
			</button>
		</nav>
	);
};
